/**
 * @documental
 * @export Tooltip
 * @import
 * import { Tooltip } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode, RefCallback, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DesignTokenProps, createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { Bounds, BoundsObserver } from "../../utilities/BoundsObserver";
import { debounce } from "../../utilities/debounce";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { noOp } from "../../utilities/noOp";
import { Point } from "./Point";
import { TooltipWindow } from "./TooltipWindow";
import { getAdjustedTooltipPosition, getOriginFromBounds } from "./helpers";
import { useTooltipContext } from "./useTooltipContext";
import { useTooltipStyleContext } from "./useTooltipStyleContext";

import tokenStyles from "./styles.module.css";

const useResolvedTooltipStyles = createComponentStyleHook<TooltipProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "tooltip",
});

/**
 * The tooltip component properties.
 */
export interface TooltipProps extends BaseProps, DesignTokenProps {
  /**
   * The alignment of the tooltip relative to the element. If `"top"`, `"bottom"`, `"left"` or `"right"` are chosen, then the tooltip will
   * attach itself to the specified edge of the target element. If `"cursor"` is specified then the tooltip will track the
   * mouse cursor position when hovering over the target element. Defaults to `"cursor"` if not specified.
   */
  readonly alignment?: "top" | "bottom" | "left" | "right" | "cursor";

  /**
   * The appearance of the tooltip. Defaults to `"dark"` if not specified.
   */
  readonly appearance?: "dark" | "light";

  /**
   * The tooltip content.
   */
  readonly content?: ReactNode;

  /**
   * Gets the element that owns the tooltip.
   */
  readonly children: (ref: RefCallback<HTMLElement>) => ReactNode;
}

/**
 * Creates a tooltip for the specified element. Takes a template function as its child which is passed a ref callback function
 * as its first parameter, allowing you to assign the element to attach the tooltip to via the React `ref` property.
 */
const TooltipInternal: FC<TooltipProps> = (props) => {
  const tooltipWindow = window as TooltipWindow;
  const tooltipId = useId();
  const { mode, styles, StyleModeBoundary } = useResolvedTooltipStyles(props);

  const { content, children, alignment, appearance } = props;

  const { element } = useTooltipContext();
  const { alignment: styleAlignment, appearance: styleAppearance } = useTooltipStyleContext();

  const calculatedAlignment = alignment === undefined ? styleAlignment : alignment;
  const calculatedAppearance = appearance === undefined ? styleAppearance : appearance;

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Point | null>(null);
  const [connectorOffset, setConnectorOffset] = useState<number>(0);
  const elementRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLElement | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const originPointRef = useRef<Point | null>(null);
  const containerBoundsRef = useRef<Bounds | null>(null);
  const elementBoundsRef = useRef<Bounds | null>(null);
  const tooltipBoundsRef = useRef<Bounds | null>(null);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Tooltip, "tooltip", calculatedAppearance, `alignment-${calculatedAlignment}`, { visible: !!position }],
    testIdPrefix: "tooltip",
  });

  // **** Begin section for optimization of memoized callbacks
  const containerRef = useRef<HTMLElement | null>(null);

  const alignmentRef = useRef(calculatedAlignment);
  useEffect(() => {
    alignmentRef.current = calculatedAlignment;
  }, [calculatedAlignment]);
  // **** End section for optimization of memoized callbacks

  const doTooltipPositionUpdate = useCallback(() => {
    if (!originPointRef.current || !tooltipBoundsRef.current || !containerBoundsRef.current) {
      setPosition(null);
      // set arrow position.
      return;
    }

    const [newPosition, newOffset] = getAdjustedTooltipPosition(
      originPointRef.current,
      tooltipBoundsRef.current,
      containerBoundsRef.current,
      alignmentRef.current,
    );

    setPosition((position) => {
      if (!position || position.x !== newPosition.x || position.y !== newPosition.y) return newPosition;
      return position;
    });

    if (alignmentRef.current === "top" || alignmentRef.current === "bottom") {
      setConnectorOffset(newOffset.x);
    } else if (alignmentRef.current === "left" || alignmentRef.current === "right") {
      setConnectorOffset(newOffset.y);
    }
  }, []);

  const [boundsObserver] = useState<BoundsObserver>(
    () =>
      new BoundsObserver((entries) => {
        for (const entry of entries) {
          const { target, bounds } = entry;
          if (target === elementRef.current) elementBoundsRef.current = bounds;
          if (target === tooltipRef.current) tooltipBoundsRef.current = bounds;
          if (target === containerRef.current) containerBoundsRef.current = bounds;
        }

        if (elementBoundsRef.current && alignmentRef.current !== "cursor") {
          originPointRef.current = getOriginFromBounds(elementBoundsRef.current, alignmentRef.current);
        }

        doTooltipPositionUpdate();
      }),
  );

  useEffect(() => {
    if (containerRef.current) {
      boundsObserver.unobserve(containerRef.current);
      containerBoundsRef.current = null;
    }

    if (element) {
      boundsObserver.observe(element);
    }

    containerRef.current = element;
  }, [element]);

  // Cleanup
  useEffect(() => {
    return (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      boundsObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    /* Synchronously collect container and element bounds to avoid race condition
       where the tooltip renders before the BoundsObserver has fired its callback.
       This ensures we have valid bounds for position calculation on the first render. */
    if (containerRef.current) {
      const { top, left, width, height } = containerRef.current.getBoundingClientRect();
      containerBoundsRef.current = { top, left, width, height };
    }

    if (elementRef.current) {
      const { top, left, width, height } = elementRef.current.getBoundingClientRect();
      elementBoundsRef.current = { top, left, width, height };

      if (alignmentRef.current !== "cursor") {
        originPointRef.current = getOriginFromBounds(elementBoundsRef.current, alignmentRef.current);
      }
    }
    boundsObserver.resume();

    return (): void => {
      boundsObserver.pause();
    };
  }, [visible]);

  const mouseMoveHandler = useCallback(
    debounce((ev: MouseEvent): void => {
      if (calculatedAlignment !== "cursor") return;
      const { clientX, clientY } = ev;
      originPointRef.current = { x: clientX, y: clientY };
      doTooltipPositionUpdate();
    }),
    [calculatedAlignment],
  );

  const mouseEnterHandler = useCallback((): void => {
    if (timeoutRef.current !== null) return;

    if (tooltipWindow["$tooltipElement"]) {
      setVisible(true);
      tooltipWindow["$tooltipElement"] = elementRef.current;
    } else {
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        setVisible(true);
        tooltipWindow["$tooltipElement"] = elementRef.current;
      }, 100);
    }
  }, []);

  const mouseLeaveHandler = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setVisible(false);
  }, []);

  const focusHandler = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setVisible(true);
      tooltipWindow["$tooltipElement"] = elementRef.current;
    }, 100);
  }, []);

  const blurHandler = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setVisible(false);
    tooltipWindow["$tooltipElement"] = null;
  }, []);

  useEffect(() => {
    let reshowTimeout: ReturnType<typeof setTimeout> | null = null;

    if (!visible && tooltipWindow["$tooltipElement"] && !reshowTimeout) {
      reshowTimeout = setTimeout(() => {
        reshowTimeout = null;
        if (tooltipWindow["$tooltipElement"] !== elementRef.current) return;
        tooltipWindow["$tooltipElement"] = null;
      }, 500);
    } else if (visible) {
      tooltipWindow["$tooltipElement"] = elementRef.current;
    }

    return (): void => {
      if (reshowTimeout !== null) {
        clearTimeout(reshowTimeout);
        reshowTimeout = null;
      }
    };
  }, [visible]);

  // Registers events and to observe the element we're attaching to for its position/bounds changing.
  const elementRefHandler = useCallback(
    (ref: HTMLElement | null): void => {
      if (elementRef.current) {
        elementRef.current.removeEventListener("mouseover", mouseEnterHandler);
        elementRef.current.removeEventListener("mousemove", mouseMoveHandler);
        elementRef.current.removeEventListener("mouseleave", mouseLeaveHandler);
        elementRef.current.removeAttribute("aria-describedby");
        elementRef.current.removeEventListener("focus", focusHandler);
        elementRef.current.removeEventListener("blur", blurHandler);
        boundsObserver.unobserve(elementRef.current);
        elementBoundsRef.current = null;
      }

      elementRef.current = ref;

      if (ref) {
        boundsObserver.observe(ref);
        ref.addEventListener("mouseover", mouseEnterHandler);
        ref.addEventListener("mousemove", mouseMoveHandler);
        ref.addEventListener("mouseleave", mouseLeaveHandler);
        ref.addEventListener("focus", focusHandler);
        ref.addEventListener("blur", blurHandler);
        if (content) {
          ref.setAttribute("aria-describedby", tooltipId);
        }
      }
    },
    [content, tooltipId],
  );

  // Registers to observe the tooltip for its position/bounds changing.
  const tooltipRefHandler = useCallback(
    (ref: HTMLElement | null): void => {
      if (tooltipRef.current) {
        boundsObserver.unobserve(tooltipRef.current);
        tooltipBoundsRef.current = null;
      }

      if (ref) {
        boundsObserver.observe(ref);
        /* Synchronously get tooltip bounds and update position to avoid race condition
         where the tooltip is visible but position is null because the BoundsObserver
         hasn't fired yet (it waits for the next animation frame). */
        const { top, left, width, height } = ref.getBoundingClientRect();
        tooltipBoundsRef.current = { top, left, width, height };
        doTooltipPositionUpdate();
      }

      tooltipRef.current = ref;
    },
    [doTooltipPositionUpdate],
  );

  const connectorStyle = useMemo(() => {
    if (calculatedAlignment === "top" || calculatedAlignment === "bottom") return { left: `${connectorOffset}px` };
    if (calculatedAlignment === "left" || calculatedAlignment === "right") return { top: `${connectorOffset}px` };

    return undefined;
  }, [calculatedAlignment, connectorOffset]);

  useEffect(() => {
    return (): void => {
      if (elementRef.current) {
        elementRef.current.removeAttribute("aria-describedby");
      }
    };
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      {children(elementRefHandler)}
      {!!element
        && visible
        && !!content
        && createPortal(
          <div
            ref={tooltipRefHandler}
            {...baseHtmlProps}
            role="tooltip"
            aria-hidden={!visible}
            id={tooltipId}
            style={{
              ...(baseHtmlProps.style ?? {}),
              ...(position ? { transform: `translate(${position.x}px, ${position.y}px)` } : {}),
            }}
          >
            <Body size="small" inline>
              {content}
            </Body>
            {calculatedAlignment !== "cursor" && (
              <div className={classNames(styles.TooltipConnector, "connector")} style={connectorStyle} aria-hidden="true" />
            )}
          </div>,
          element,
        )}
    </StyleModeBoundary>
  );
};

/**
 * Creates a tooltip for the specified element. Takes a template function as its child which is passed a ref callback function
 * as its first parameter, allowing you to assign the element to attach the tooltip to via the React `ref` property.
 */
export const Tooltip: FC<TooltipProps> = (props) => {
  const { content, children } = props;

  if (content === null || content === undefined || content === false || content === "") {
    return <>{children(noOp)}</>;
  }

  return <TooltipInternal {...props}>{children}</TooltipInternal>;
};
